import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";
import { i18n } from "@/i18n";
import { showConfirm } from "@core/ui/alert/alert";

import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";

import type { ScenarioItem } from "./ScenarioItem";
import { useScenarioEndStore } from "../scenarioEnd/scenarioEnds.store";

const itemStructure: ScenarioItem = {
  type: "scenario",
  id: 0,
  uid: "scenario_0",
  createdAt: 0,

  title: "",
  description: "",

  incidentIds: [],
  actionIds: [],
  autoAddIncidents: true,
  autoAddActions: true,

  resourceValues: {},
  targetValues: {},
  endStates: {},

  showStartMessage: true,
  startTitle: undefined,
  startText: undefined,
  startImageId: undefined,

  endTitle: undefined,
  endText: undefined,
  endImageId: undefined,

  effectsVisibility: "as-is",

  // simulation round settings:
  maxRounds: null,
  roundUnit: null,
  roundLength: null,
  roundAmount: null,
  durationType: null,
  timeStart: null,
  timeEnd: null,
  roundCustomized: null,

  // impulsesVisibility
  impulsesFromImpactsVisible: true,
  impulsesFromFlowsVisible: true,
  impulsesFromMomentumsVisible: true,
  impulsesFromEventsVisible: true,
  impulsesFromActionsVisible: true,

  order: 0,
  docs: {}
};

export const useScenarioStore = defineItemStore(
  "model/scenarios",
  itemStructure,
  {
    state: (baseState) => {
      // add first item:
      baseState.count += 1;
      baseState.items[0] = {
        ...itemStructure,
        id: 0,
        uid: "scenario_0",
        title: "Standard"
      };
      baseState.order = [0];

      return {};
    },
    getters: {
      /**
       * Get all scenarios that are setting a different start value
       * for the given resource.
       */
      getChangingResource() {
        return (resourceId: number) =>
          this.orderedItems.filter((scenario) =>
            Object.keys(scenario.resourceValues).includes(resourceId + "")
          );
      },
      getByAction() {
        return (actionId: number) =>
          this.orderedItems.filter((scenario) =>
            scenario.actionIds.includes(actionId)
          );
      },
      getByIncident() {
        return (incidentId: number) =>
          this.orderedItems.filter((scenario) =>
            scenario.incidentIds.includes(incidentId)
          );
      }
    },
    events: {
      async canBeDeleted(scenario, scenarioStore) {
        // make sure there will always remain one scenario
        if (scenarioStore.orderedItems.length <= 1) {
          return false;
        }

        const simulationsAmount = useSimulationStore().length;
        if (simulationsAmount === 0) return true;

        return await showConfirm(
          i18n.t("questions.deleteScenario"),
          i18n.t("actions.deleteScenario"),
          i18n.t("labels.abort")
        );
      },
      async beforeCreate(scenario) {
        const actionStore = useActionStore();
        // add all actions and incidents to any new scenario.
        const allActionIds = actionStore.ids;
        scenario.actionIds = allActionIds.slice();

        const incidentStore = useIncidentStore();
        const allIncidentIds = incidentStore.ids;
        scenario.incidentIds = allIncidentIds.slice();

        return scenario;
      },
      async afterDelete(scenario) {
        // delete all simulations based on this scenario
        const simulationStore = useSimulationStore();
        const simulations = simulationStore.byScenario[scenario.id];
        if (simulations != null) {
          simulationStore.deleteMany(simulations);
        }

        // delete connected IMAGES
        const imageStore = useImageStore();
        if (scenario.startImageId != null) {
          imageStore.delete({ id: scenario.startImageId, instant: true });
        }
        if (scenario.endImageId != null) {
          imageStore.delete({ id: scenario.endImageId, instant: true });
        }
      }
    },
    actions: {
      connectIncident({ id, incidentId }: { id: number; incidentId: number }) {
        this.items[id].incidentIds.push(incidentId);
      },
      disconnectIncident({
        id,
        incidentId
      }: {
        id: number;
        incidentId: number;
      }) {
        const ids = this.items[id].incidentIds.slice(0);
        ids.splice(ids.indexOf(incidentId), 1);
        this.items[id].incidentIds = ids;
      },

      connectAction({ id, actionId }: { id: number; actionId: number }) {
        this.items[id].actionIds.push(actionId);
      },
      disconnectAction({ id, actionId }: { id: number; actionId: number }) {
        const ids = this.items[id].actionIds.slice(0);
        ids.splice(ids.indexOf(actionId), 1);
        this.items[id].actionIds = ids;
      },

      setResourceValue({
        id,
        resourceId,
        value
      }: {
        id: number;
        resourceId: number;
        value: number;
      }) {
        this.items[id].resourceValues[resourceId] = value;
      },
      deleteResourceValue({
        id,
        resourceId
      }: {
        id: number;
        resourceId: number;
      }) {
        delete this.items[id].resourceValues[resourceId];
      },

      setTargetValue({
        id,
        targetId,
        value
      }: {
        id: number;
        targetId: number;
        value: number;
      }) {
        this.items[id].targetValues[targetId] = value;
      },
      deleteTargetValue({ id, targetId }: { id: number; targetId: number }) {
        delete this.items[id].targetValues[targetId];
      },

      setEndState({
        id,
        conditionId,
        value
      }: {
        id: number;
        conditionId: number;
        value: number;
      }) {
        const scenario = this.items[id];
        scenario.endStates[conditionId] = value;
      },
      deleteEndState({ id, conditionId }: { id: number; conditionId: number }) {
        const scenario = this.items[id];
        delete scenario.endStates[conditionId];
      },
      /**
       * Used for auto-adding new incidents on create to all scenarios.
       */
      connectAllToIncident({ incidentId }: { incidentId: number }) {
        for (const scenario of this.orderedItems) {
          if (scenario.autoAddIncidents) {
            this.connectIncident({ id: scenario.id, incidentId });
          }
        }
      },
      /**
       * Used for auto-adding new actions on create to all scenarios.
       */
      connectAllToAction({ actionId }: { actionId: number }) {
        for (const scenario of this.orderedItems) {
          if (scenario.autoAddActions) {
            this.connectAction({ id: scenario.id, actionId });
          }
        }
      },

      async duplicate(id: number, changes: Partial<ScenarioItem> = {}) {
        const source = this.items[id];
        if (source == null) throw new Error("Scenario not found");

        const newScenario = await this.super.duplicate(id, {
          startImageId: null,
          endImageId: null,
          ...changes
        });

        // duplicate the ends
        const scenarioEndStore = useScenarioEndStore();
        const scenarioEnds = scenarioEndStore.byScenario[id] ?? [];
        for (const end of scenarioEnds) {
          scenarioEndStore.duplicate(end.id, { scenarioId: newScenario.id });
        }

        const imageStore = useImageStore();
        if (source.startImageId != null) {
          const newImage = await imageStore.duplicate(source.startImageId);
          newScenario.startImageId = newImage.id;
        }
        if (source.endImageId != null) {
          const newImage = await imageStore.duplicate(source.endImageId);
          newScenario.endImageId = newImage.id;
        }

        return newScenario;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHotItemStore(useScenarioStore, import.meta.hot));
}
